/**
 * Shared CRM contract primitives. Destination: `@renote/contracts`.
 *
 * Convention: Postgres-nullable columns use `.nullable()` (-> `T | null`), never
 * `.optional()`. Optionality is reserved for keys that may be genuinely absent
 * from an input payload, which keeps "not supplied" distinguishable from
 * "explicitly cleared" under `exactOptionalPropertyTypes`.
 */

import { z } from 'zod'

export const uuid = z.string().uuid()

/** Trims, then treats an all-whitespace string as absent rather than as "". */
export const trimmedText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v: string) => (v.length === 0 ? null : v))
    .nullable()

/** Optional URL that degrades to null rather than failing the whole payload. */
export const optionalUrl = z.string().trim().url().nullable().catch(null)

/** Optional email, lowercased; same degrade-to-null behaviour. */
export const optionalEmail = z.string().trim().toLowerCase().email().nullable().catch(null)

export const currencyCode = z.string().length(3).toUpperCase()

/** ISO-8601 instant, as Postgres timestamptz serialises it. */
export const instant = z.string().datetime()

export const sortDirection = z.enum(['asc', 'desc'])
export type SortDirection = z.infer<typeof sortDirection>

/* -------------------------------------------------------------------------- *
 * Query-string parameters
 *
 * Everything arriving via `@Query()` is a string, so list schemas must coerce.
 * These helpers are no-ops when the value already has the right type, which lets
 * one schema serve both the HTTP boundary and internal callers.
 * -------------------------------------------------------------------------- */

/**
 * Boolean from a query string.
 *
 * NOT `z.coerce.boolean()` — that is `Boolean(value)`, so the string "false"
 * becomes `true` and `?converted=false` silently means the opposite of what it
 * says. This accepts the four spellings that actually appear on the wire.
 */
export const boolParam = z
  .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
  .transform((value: boolean | 'true' | 'false' | '1' | '0') =>
    typeof value === 'boolean' ? value : value === 'true' || value === '1',
  )

/** Integer from a query string, with bounds. */
export const intParam = (min: number, max: number) =>
  z.coerce.number().int().min(min).max(max)

/**
 * Repeated query parameters. `?status=a&status=b` parses as an array, but
 * `?status=a` arrives as a bare string — normalise the single case so callers
 * always see a list.
 */
export const arrayParam = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess(
    (value: unknown) =>
      value === undefined ? undefined : Array.isArray(value) ? value : [value],
    z.array(item),
  )

/**
 * A structured value carried in a query string as JSON.
 *
 * Used for filters, which are a nested array and cannot be expressed as repeated
 * scalar params. A string that will not parse is passed through unchanged so the
 * inner schema rejects it — silently dropping a malformed filter would show the
 * user an unfiltered list and look like data loss.
 */
export const jsonParam = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((value: unknown) => {
    if (typeof value !== 'string') return value
    try {
      return JSON.parse(value)
    } catch {
      return value
    }
  }, schema)

/**
 * Keyset pagination. Deliberate: it keeps list responses cheap at depth, and it
 * means the web app can render a plain table with a "load more" instead of
 * pulling in a virtualisation dependency.
 */
export const pageQuery = z.object({
  limit: intParam(1, 200).default(50),
  cursor: z.string().optional(),
})
export type PageQuery = z.infer<typeof pageQuery>

export const pageOf = <T extends z.ZodTypeAny>(row: T) =>
  z.object({
    rows: z.array(row),
    nextCursor: z.string().nullable(),
    totalCount: z.number().int().nonnegative(),
  })

/**
 * Filter operators the list-view compiler understands. Closed set by design —
 * these become SQL, so anything outside this enum must not reach the query
 * builder.
 */
export const filterOperator = z.enum([
  'eq',
  'neq',
  'in',
  'notIn',
  'contains',
  'startsWith',
  'gt',
  'gte',
  'lt',
  'lte',
  'isNull',
  'isNotNull',
  'between',
])
export type FilterOperator = z.infer<typeof filterOperator>

/**
 * A single filter clause. `field` is validated against the per-entity field
 * registry (below) server-side before compilation — never interpolate it raw.
 */
export const viewFilter = z.object({
  field: z.string().min(1).max(64),
  operator: filterOperator,
  value: z.unknown(),
})
export type ViewFilter = z.infer<typeof viewFilter>

export const viewSort = z.object({
  field: z.string().min(1).max(64),
  direction: sortDirection,
})
export type ViewSort = z.infer<typeof viewSort>

export const viewColumn = z.object({
  field: z.string().min(1).max(64),
  width: z.number().int().min(48).max(1200).nullable(),
  position: z.number().int().nonnegative(),
})
export type ViewColumn = z.infer<typeof viewColumn>

/**
 * Field registry entry. One registry per entity, defined server-side, consumed
 * by BOTH the SQL compiler (as the allowlist) and the web app (to build the
 * filter and column menus). Single source of truth — if a field is not here, it
 * is neither filterable nor displayable.
 */
export const fieldKind = z.enum([
  'text',
  'number',
  'currency',
  'date',
  'datetime',
  'boolean',
  'select',
  'user',
  'link',
])
export type FieldKind = z.infer<typeof fieldKind>

export const fieldDescriptor = z.object({
  field: z.string(),
  label: z.string(),
  kind: fieldKind,
  filterable: z.boolean(),
  sortable: z.boolean(),
  /** For 'select' / 'link': where the option list comes from. */
  optionsSource: z.string().nullable(),
})
export type FieldDescriptor = z.infer<typeof fieldDescriptor>
